import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useEntityStore, useSchemaStore } from '@ContractBuilder/store';
import type { ResourceState } from '@domain/schemas/contract.schema';
import { ContractError } from '@domain/types/errors';
import { DocumentMode } from '@helpers/document-mode';
import { getDocumentMode } from '@helpers/getDocumentMode';
import { getBlockValidationErrors, getContractValidationErrors, isDocumentValid } from '@helpers/status';
import { useEffectOnce } from '@src/hooks';

import { getConfig } from '../utils/getConfig';
import { ConfirmStatusFirmOrderModal } from '../views/ConfirmStatusFirmOrderModal';

interface ConfirmStatusModalProps {
  handleClose: () => void;
  onConfirm: () => Promise<void>;
  onConfirmByPassValidation: () => Promise<void>;
  targetStatus: ResourceState;
}

export const ConfirmStatusFirmOrderModalController: FC<ConfirmStatusModalProps> = ({
  handleClose,
  onConfirm,
  onConfirmByPassValidation,
  targetStatus,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedSubmission, setHasLoadedSubmission] = useState(false);

  const handleConfirm = useCallback(async () => {
    setIsLoading(true);
    await onConfirm();
    setIsLoading(false);
    handleClose();
  }, [onConfirm, handleClose]);

  const handleConfirmByPassValidation = useCallback(async () => {
    setIsLoading(true);
    await onConfirmByPassValidation();
    setIsLoading(false);
    handleClose();
  }, [onConfirmByPassValidation, handleClose]);

  const { submission, reloadSubmission } = useEntityStore(({ submission, reloadSubmission }) => ({
    submission,
    reloadSubmission,
  }));

  const { mode } = getDocumentMode();
  const { isValid, validate } = useSchemaStore(({ isValid, validate }) => ({ isValid, validate }));

  const isSubmission = mode === DocumentMode.SUBMISSIONS;
  const isEndorsement = mode === DocumentMode.ENDORSEMENTS;

  const blockErrors = useMemo(() => {
    return getBlockValidationErrors(isSubmission || isEndorsement, submission);
  }, [isSubmission, isEndorsement, submission]);

  const contractErrors = useMemo(() => {
    return getContractValidationErrors(isSubmission || isEndorsement, isValid, submission);
  }, [isSubmission, isEndorsement, isValid, submission]);

  const skipReload = isEndorsement && submission?.hasMaterialChanges === false;

  useEffectOnce(() => {
    (async () => {
      if (isSubmission || isEndorsement) {
        if (!skipReload) {
          await reloadSubmission();
        }
        validate();

        setHasLoadedSubmission(true);
      }
    })();
  });

  const isDocValid = isDocumentValid(blockErrors, contractErrors);
  const hasMissingDataPoints = contractErrors.some((err) => err.type === ContractError.MissingDatapoints);
  const hasNoMaterialChanges = contractErrors.some((err) => err.type === ContractError.NoMaterialChanges);

  const config = getConfig({
    isDocValid,
    documentMode: mode,
    hasMissingDataPoints,
    hasNoMaterialChanges,
    status: submission?.status,
    targetStatus,
    onClose: handleClose,
    onSubmit: handleConfirm,
    onSubmitByPassValidation: handleConfirmByPassValidation,
  });

  if (!config) {
    return null;
  }

  return (
    <ConfirmStatusFirmOrderModal
      hasLoadedSubmission={hasLoadedSubmission}
      isLoading={isLoading}
      onClose={handleClose}
      blockErrors={blockErrors}
      contractErrors={contractErrors}
      submission={submission}
      config={config}
    />
  );
};
